import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../utils/api'; // Your configured HTTP client (e.g., axios instance)

// Asynchronous thunk to fetch wall art/photo prints products from your Django backend
export const fetchWallArtPhotoPrints = createAsyncThunk(
  'wallArtPhotoPrints/fetchWallArtPhotoPrints',
  async (_, { rejectWithValue }) => {
    try {
      // Adjust the URL below to match your Django API endpoint (e.g., /api/wall-art-photo-prints/)
      const response = await api.get('/api/wall-art-photo-prints/');
      return response.data; // Expected to return an array of product objects
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch wall art/photo prints products'
      );
    }
  }
);

// Define the initial filter state. Adjust keys as needed.
const initialFilters = {
  personalize: false,
  productTypes: { hoodies: false, sweatshirts: false }, // Update if needed for wall art prints
  sizes: { XS: false, S: false, M: false, L: false, XL: false, '2XL': false, '3XL': false },
  colors: { black: false, green: false, yellow: false, red: false, purple: false, custom: false },
  fabric: { cotton: false, polyester: false, cottonPolyBlend: false },
  printMethod: { screenPrinting: false, dtg: false, dtf: false, embroidery: false, heatTransfer: false },
  printPlacement: { front: false, back: false, sleeve: false, hood: false },
  customization: { oneColor: false, fullColor: false, logoOnly: false },
  orientation: '',
  corners: '',
  designStyle: { inter: false, prata: false, protestRevolution: false, promo: false },
  priceRange: 100,
  rates: { five: false, four: false, three: false },
};

// Initial state starts with an empty products array – data will be loaded via the API.
const initialState = {
  loading: false,
  error: null,
  products: [],
  filteredProducts: [],
  filters: initialFilters,
};

const wallArtPhotoPrintsSlice = createSlice({
  name: 'wallArtPhotoPrints',
  initialState,
  reducers: {
    // Update a top‑level boolean filter (e.g., "personalize")
    setSimpleFilter(state, action) {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    // Update a nested filter property (e.g., within productTypes, sizes, colors, etc.)
    setNestedFilter(state, action) {
      const { category, key, value } = action.payload;
      state.filters[category][key] = value;
    },
    // Update a radio filter (for orientation, corners, etc.)
    setRadioFilter(state, action) {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    // Update the range filter (e.g., price range)
    setRangeFilter(state, action) {
      state.filters.priceRange = action.payload;
    },
    // Reset all filters to the initial defaults and display all products
    resetFilters(state) {
      state.filters = { ...initialFilters };
      state.filteredProducts = state.products;
    },
    // Apply filtering logic based on the current filters
    applyFilters(state) {
      const { filters, products } = state;
      
      const selectedTypes = Object.entries(filters.productTypes)
        .filter(([_, value]) => value)
        .map(([key]) => key);
      
      const selectedSizes = Object.entries(filters.sizes)
        .filter(([_, value]) => value)
        .map(([key]) => key);
      
      const selectedRates = Object.entries(filters.rates)
        .filter(([_, value]) => value)
        .map(([key]) => {
          if (key === 'five') return 5;
          if (key === 'four') return 4;
          if (key === 'three') return 3;
          return null;
        })
        .filter((rate) => rate !== null);
      
      state.filteredProducts = products.filter((product) => {
        // If any product type is selected, check if product.type matches
        if (selectedTypes.length > 0) {
          const prodTypeKey = product.type === 'hoodie' ? 'hoodies' : 'sweatshirts';
          if (!selectedTypes.includes(prodTypeKey)) return false;
        }
        
        // Check sizes
        if (selectedSizes.length > 0) {
          if (!product.sizes.some((size) => selectedSizes.includes(size))) return false;
        }
        
        // Check price range
        if (product.price > filters.priceRange) return false;
        
        // Check rating
        if (selectedRates.length > 0) {
          if (!selectedRates.includes(product.rating)) return false;
        }
        
        // Additional filters (e.g., colors, fabric, etc.) can be added as needed
        return true;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWallArtPhotoPrints.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWallArtPhotoPrints.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
        state.filteredProducts = action.payload;
      })
      .addCase(fetchWallArtPhotoPrints.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  setSimpleFilter,
  setNestedFilter,
  setRadioFilter,
  setRangeFilter,
  resetFilters,
  applyFilters
} = wallArtPhotoPrintsSlice.actions;

export default wallArtPhotoPrintsSlice.reducer;
